import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, useRef, useState } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import { asArray } from '@olivierpascal/helpers';
import { useControlledValue } from '../../hooks/useControlledValue';
import { isValidHexColor } from '../../helpers/colors/isValidHexColor';
import { useStyles } from '../../hooks/useStyles';
import { extractPaletteFromImage } from '../../helpers/colors/extractPaletteFromImage';
import { iconPhoto } from '../../assets/icons';
import { HslColorPickerContent } from '../HslColorPickerContent';
import { TextInputField } from '../TextInputField';
import { PopoverBase } from '../PopoverBase';
import { ColorTag } from '../ColorTag';
import { IconButton } from '../IconButton';
import { SvgIcon } from '../SvgIcon';
import { ColorPaletteGroupContext } from '../ColorPaletteGroup';
import { colorInputFieldStyles } from './ColorInputField.styles';
const defaultColorPickerRenderer = (props) => _jsx(HslColorPickerContent, { ...props });
export const ColorInputField = forwardRef(function Field(props, forwardedRef) {
    const { styles, sx, placement = 'bottom-start', transitionOrientation = 'vertical', value: valueProp, defaultValue, inputRef: inputRefProp, colorPickerRenderer = defaultColorPickerRenderer, customColors: customColorsProp, onColorsQuantized, quantizeColorCount = 8, ...other } = props;
    const { combineStyles, globalStyles } = useStyles({
        name: 'ColorInputField',
        styles: [colorInputFieldStyles, styles],
    });
    const [value, setValue] = useControlledValue({
        controlled: valueProp,
        default: defaultValue ?? '',
        name: 'ColorInputField',
        onValueChange: (color) => other.onChange?.(color),
    });
    const [quantizedColors, setQuantizedColors] = useState();
    const [isQuantizing, setIsQuantizing] = useState(false);
    const inputRef = useRef(null);
    const inputHandleRef = useMergeRefs([inputRef, ...asArray(inputRefProp)]);
    const inputFileRef = useRef(null);
    const colorPaletteGroupContext = useContext(ColorPaletteGroupContext);
    const handleUploadImage = () => {
        inputFileRef.current?.click();
    };
    const handleFileChange = (event) => {
        const files = event.target.files;
        const file = files?.[0];
        if (!file) {
            return;
        }
        setIsQuantizing(true);
        const fileReader = new FileReader();
        fileReader.onload = () => {
            const image = new Image();
            image.src = fileReader.result;
            void extractPaletteFromImage(image, quantizeColorCount)
                .then((quantizedPalette) => {
                setQuantizedColors(quantizedPalette);
                setValue(quantizedPalette[0]);
                onColorsQuantized?.(quantizedPalette);
                colorPaletteGroupContext?.setQuantizedPalette(quantizedPalette);
            })
                .finally(() => setIsQuantizing(false));
        };
        fileReader.readAsDataURL(file);
    };
    const customColors = [
        ...(customColorsProp ?? []),
        ...((colorPaletteGroupContext ? undefined : quantizedColors) ?? []),
    ];
    return (_jsxs(_Fragment, { children: [_jsx("input", { type: 'file', ref: inputFileRef, accept: 'image/*', style: { display: 'none' }, onChange: handleFileChange }), _jsx(PopoverBase, { contentRenderer: ({ close }) => colorPickerRenderer({
                    onClick: (_, colorHex) => {
                        setValue(colorHex);
                        close();
                    },
                    selectedColor: value,
                    customColors,
                }), placement: placement, transitionOrientation: transitionOrientation, openEvents: { click: true }, trapFocus: true, children: ({ getProps, setRef }) => (_jsx(TextInputField, { start: _jsx(ColorTag, { backgroundColor: value, sx: combineStyles('colorTag') }), end: _jsx(IconButton, { icon: _jsx(SvgIcon, { icon: iconPhoto }), onClick: (event) => {
                            handleUploadImage(event);
                        }, loading: isQuantizing }), autoComplete: 'off', ...other, ...getProps(), sx: [globalStyles, combineStyles('host'), sx], value: value, hasError: (!!value && !isValidHexColor(value)) || other.hasError, onChange: (event) => setValue(event.target.value), ref: forwardedRef, inputRef: inputHandleRef, containerRef: setRef })) })] }));
});
